import React, { useMemo } from 'react';
import { C, S } from '../utils/styles';
import { fmt, fmtK } from '../utils/formatters';
import Icon from './Icon';
import { optimizeEmployeeWithEligibility } from '../utils/calculations';

/**
 * Eligibility Maximization Advisor
 * Analyzes workforce and provides actionable recommendations to increase participation
 */
export default function EligibilityAdvisor({ employees, config, analyzed, mode = 'internal' }) {
  
  // Generate all recommendations
  const recommendations = useMemo(() => {
    const recs = [];
    
    // Recommendation 1: Waiting Period Optimization
    const waitingEmployees = analyzed.filter(e => e.eligibility?.status === 'waiting');
    if (waitingEmployees.length > 0) {
      const sortedByEligibleDate = [...waitingEmployees].sort((a, b) => 
        (a.eligibility.eligibleDate?.getTime() || 0) - (b.eligibility.eligibleDate?.getTime() || 0)
      );
      const latestDate = sortedByEligibleDate[sortedByEligibleDate.length - 1]?.eligibility?.eligibleDate;
      const maxDays = latestDate ? Math.ceil((latestDate - new Date()) / (1000 * 60 * 60 * 24)) : 0;
      
      // Calculate impact at different waiting period scenarios
      const waitingPeriodScenarios = config.advisorSettings?.waitingPeriodScenarios || [0, 30, 60, 90];
      const currentWaitingPeriod = config.employerPlanRules.waiting_period_days;
      const relevantScenarios = waitingPeriodScenarios.filter(days => days < currentWaitingPeriod);
      
      // Calculate potential impact for each scenario
      const scenarioImpacts = relevantScenarios.map(days => {
        let eligibleCount = 0;
        let ficaSavings = 0;
        
        waitingEmployees.forEach(e => {
          const daysWorked = e.hireDate ? Math.floor((new Date() - new Date(e.hireDate)) / (1000 * 60 * 60 * 24)) : 0;
          if (daysWorked >= days) {
            const simulated = optimizeEmployeeWithEligibility(
              { ...e, hireDate: new Date(Date.now() - (days + 1) * 24 * 60 * 60 * 1000) },
              config
            );
            if (simulated.hasViable && simulated.optimal) {
              eligibleCount++;
              ficaSavings += simulated.optimal.employerAnnualFica || 0;
            }
          }
        });
        
        return { days, eligibleCount, ficaSavings };
      });
      
      recs.push({
        id: 'waiting-period',
        title: 'Reduce Waiting Period',
        impact: 'high',
        count: waitingEmployees.length,
        // Positive-ROI: waiting employees become eligible automatically as time
        // passes — there's no employer cost to reducing the waiting period
        // beyond a policy update. Best-scenario eligibleCount is the realistic
        // contribution to the program ceiling.
        positiveRoiCount: scenarioImpacts.length > 0 ? Math.max(...scenarioImpacts.map((s) => s.eligibleCount || 0)) : 0,
        type: 'policy',
        content: (
          <div>
            <p style={{ fontSize: 13, color: C.textMid, marginBottom: 12, lineHeight: 1.6 }}>
              <strong>{waitingEmployees.length} employees</strong> are currently in their waiting period and will become eligible within the next <strong>{maxDays} days</strong>.
            </p>
            <div style={{ background: C.bg, padding: '12px 14px', borderRadius: 8, marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                Action
              </div>
              <div style={{ fontSize: 12, color: C.text, lineHeight: 1.5 }}>
                Consider reducing the waiting period from <strong>{config.employerPlanRules.waiting_period_days} days</strong> to a shorter duration.
              </div>
            </div>
            <div style={{ background: C.greenLight, padding: '12px 14px', borderRadius: 8, marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.green, marginBottom: 8 }}>
                Potential Impact by Waiting Period
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {scenarioImpacts.map(({ days, eligibleCount, ficaSavings }) => (
                  <div key={days} style={{ fontSize: 12, color: C.textMid, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span><strong>At {days} days:</strong></span>
                    <span>+{eligibleCount} employees → {fmtK(ficaSavings)}/year FICA savings</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ fontSize: 11, color: C.textLight, lineHeight: 1.5 }}>
              <strong>Timeline:</strong> These {waitingEmployees.length} employees will become eligible automatically by {latestDate?.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })} under the current {config.employerPlanRules.waiting_period_days}-day waiting period.
            </div>
          </div>
        ),
      });
    }
    
    // Recommendation 2: Hours Threshold Optimization
    const belowHoursEmployees = analyzed.filter(e => e.eligibility?.status === 'hours');
    if (belowHoursEmployees.length > 0) {
      const hoursRange = belowHoursEmployees.map(e => e.weeklyHours);
      const lowestHours = Math.min(...hoursRange);
      const currentThreshold = config.employerPlanRules.minimum_weekly_hours;
      
      // Calculate impact at different thresholds
      const hoursScenarios = config.advisorSettings?.hoursScenarios || [20, 25, 30];
      const thresholds = hoursScenarios.filter(t => t < currentThreshold).sort((a, b) => b - a);
      const impactByThreshold = thresholds.map(threshold => {
        let count = 0;
        let ficaSavings = 0;
        
        belowHoursEmployees.forEach(e => {
          if (e.weeklyHours >= threshold) {
            // Simulate with lower threshold
            const simulated = optimizeEmployeeWithEligibility(e, {
              ...config,
              employerPlanRules: {
                ...config.employerPlanRules,
                minimum_weekly_hours: threshold,
              }
            });
            if (simulated.hasViable && simulated.optimal) {
              count++;
              ficaSavings += simulated.optimal.employerAnnualFica || 0;
            }
          }
        });
        
        return { threshold, count, ficaSavings };
      });
      
      if (impactByThreshold.some(i => i.count > 0)) {
        recs.push({
          id: 'hours-threshold',
          title: 'Adjust Minimum Hours Requirement',
          impact: impactByThreshold[0]?.count >= 5 ? 'high' : 'medium',
          count: belowHoursEmployees.length,
          // Policy change has no direct employer cost; the best-scenario count
          // is the realistic positive-ROI contribution.
          positiveRoiCount: Math.max(0, ...impactByThreshold.map((s) => s.count || 0)),
          type: 'policy',
          content: (
            <div>
              <p style={{ fontSize: 13, color: C.textMid, marginBottom: 12, lineHeight: 1.6 }}>
                <strong>{belowHoursEmployees.length} employees</strong> work between <strong>{lowestHours}</strong> and <strong>{currentThreshold - 1} hours per week</strong>, just below the current {currentThreshold}-hour minimum.
              </p>
              <div style={{ background: C.bg, padding: '12px 14px', borderRadius: 8, marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                  Action
                </div>
                <div style={{ fontSize: 12, color: C.text, lineHeight: 1.5 }}>
                  Reducing the minimum weekly hours from <strong>{currentThreshold}</strong> would qualify additional employees.
                </div>
              </div>
              <div style={{ background: C.greenLight, padding: '12px 14px', borderRadius: 8, marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.green, marginBottom: 8 }}>
                  Impact by Threshold
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {impactByThreshold.map(({ threshold, count, ficaSavings }) => (
                    count > 0 && (
                      <div key={threshold} style={{ fontSize: 12, color: C.textMid, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span><strong>At {threshold} hours minimum:</strong></span>
                        <span style={{ fontWeight: 700, color: C.green }}>+{count} eligible, +{fmtK(ficaSavings)}/year FICA</span>
                      </div>
                    )
                  ))}
                </div>
              </div>
              <div style={{ fontSize: 11, color: C.textLight, lineHeight: 1.5 }}>
                <strong>Note:</strong> Ensure any threshold change is reflected in the plan document.
              </div>
            </div>
          ),
        });
      }
    }
    
    // Recommendation 3: Near-Threshold Salary Employees
    const nearEligibleBuffer = config.advisorSettings?.nearEligibleSalaryBuffer || 3000;
    const notRecommendedEmployees = analyzed.filter(e => 
      e.eligibility?.status === 'salary' && e.breakEvenSalary
    );
    
    if (notRecommendedEmployees.length > 0) {
      // Find employees within buffer of break-even
      const nearThreshold = notRecommendedEmployees.filter(e => 
        e.breakEvenSalary && (e.breakEvenSalary - e.salary) <= nearEligibleBuffer
      );
      
      if (nearThreshold.length > 0) {
        // Pre-compute the per-employee ROI so we can group positives first,
        // then the alert callout, then negatives. Sort within each group
        // by net impact (most-positive first; least-negative first).
        const enriched = nearThreshold.map((e) => {
          const gap = e.breakEvenSalary - e.salary;
          const monthlyGap = gap / 12;
          const simulated = optimizeEmployeeWithEligibility(
            { ...e, salary: e.breakEvenSalary },
            config
          );
          const ficaSavings = simulated.hasViable && simulated.optimal ? simulated.optimal.employerAnnualFica : 0;
          const netImpact = ficaSavings - gap;
          return { e, gap, monthlyGap, ficaSavings, netImpact };
        });
        const positives = enriched
          .filter((row) => row.netImpact > 0)
          .sort((a, b) => b.netImpact - a.netImpact);
        const negatives = enriched
          .filter((row) => row.netImpact <= 0)
          .sort((a, b) => b.netImpact - a.netImpact); // least-negative first

        const DISPLAY_CAP = 15;
        // Allocate display cap proportionally so we always show some of each group
        const positivesShown = positives.slice(0, Math.min(DISPLAY_CAP, positives.length));
        const remainingCap = Math.max(0, DISPLAY_CAP - positivesShown.length);
        const negativesShown = negatives.slice(0, remainingCap);
        const remainingCount =
          (positives.length - positivesShown.length) +
          (negatives.length - negativesShown.length);

        const renderRow = (row, key) => (
          <div key={key} style={{ fontSize: 12, color: C.textMid, padding: '8px 10px', background: C.card, borderRadius: 6, border: `1px solid ${row.netImpact > 0 ? C.greenBorder : C.border}` }}>
            <div style={{ fontWeight: 700, color: C.text, marginBottom: 4 }}>
              {row.e.name}: {fmt(row.e.salary)} → needs {fmt(row.gap)} more annually (${Math.round(row.monthlyGap)}/mo)
            </div>
            <div style={{ fontSize: 11, color: row.netImpact > 0 ? C.green : C.orange }}>
              ROI: Spend {fmt(row.gap)}/year in raise → Save {fmt(row.ficaSavings)}/year FICA = <strong>{row.netImpact > 0 ? `+${fmt(row.netImpact)}` : fmt(row.netImpact)} net</strong>
            </div>
          </div>
        );

        recs.push({
          id: 'near-threshold-salary',
          title: 'Near-Eligible Employees',
          impact: 'medium',
          count: nearThreshold.length,
          // Only count raises that pay for themselves (FICA recovery > raise cost).
          // Negative-ROI raises wouldn't be a reasonable employer recommendation.
          positiveRoiCount: positives.length,
          type: 'opportunity',
          content: (
            <div>
              <p style={{ fontSize: 13, color: C.textMid, marginBottom: 12, lineHeight: 1.6 }}>
                <strong>{nearThreshold.length} employees</strong> are within <strong>{fmt(nearEligibleBuffer)}</strong> of the minimum salary needed for Plan 600 eligibility.
              </p>

              {positivesShown.length > 0 && (
                <div style={{ background: C.bg, padding: '12px 14px', borderRadius: 8, marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.green, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Icon name="trending-up" style={{ fontSize: 13 }} />
                    Net positive — raise pays for itself
                    <span style={{ marginLeft: 'auto', fontSize: 10, color: C.textLight, fontWeight: 600 }}>{positives.length} employee{positives.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {positivesShown.map((row, i) => renderRow(row, `pos-${i}`))}
                  </div>
                </div>
              )}

              {positivesShown.length > 0 && (
                <div style={{ background: C.greenLight, padding: '10px 12px', borderRadius: 8, fontSize: 12, color: C.green, fontWeight: 600, marginBottom: 12 }}>
                  <Icon name="trending-up" style={{ fontSize: 14, marginRight: 4 }} />
                  A salary adjustment for these employees would pay for itself through FICA savings alone.
                </div>
              )}

              {negativesShown.length > 0 && (
                <div style={{ background: C.bg, padding: '12px 14px', borderRadius: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.orange, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Icon name="alert-circle-outline" style={{ fontSize: 13 }} />
                    Net cost — raise exceeds FICA recovery
                    <span style={{ marginLeft: 'auto', fontSize: 10, color: C.textLight, fontWeight: 600 }}>{negatives.length} employee{negatives.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {negativesShown.map((row, i) => renderRow(row, `neg-${i}`))}
                  </div>
                </div>
              )}

              {remainingCount > 0 && (
                <div style={{ fontSize: 11, color: C.textLight, fontStyle: 'italic', textAlign: 'center', padding: '8px 0 0' }}>
                  + {remainingCount} more near-eligible employee{remainingCount !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          ),
        });
      }
    }
    
    // Recommendation 5: 1099 Reclassification Opportunity (Informational)
    const contractors = analyzed.filter(e => e.eligibility?.status === '1099');
    if (contractors.length > 0) {
      let potentialFicaSavings = 0;
      const contractorDetails = contractors.slice(0, 5).map(e => {
        // Simulate as W-2
        const simulated = optimizeEmployeeWithEligibility(
          { ...e, workerType: 'W-2' },
          config
        );
        const ficaSavings = simulated.hasViable && simulated.optimal ? simulated.optimal.employerAnnualFica : 0;
        potentialFicaSavings += ficaSavings;
        return { ...e, ficaSavings, simulatedPlan: simulated.optimal?.label };
      });
      
      recs.push({
        id: '1099-reclassification',
        title: '1099 Worker Reclassification',
        impact: 'informational',
        count: contractors.length,
        // Reclassification triggers new employer FICA on the full salary plus
        // unemployment/workers-comp obligations. The Section 125 FICA savings
        // alone rarely outweigh those costs — and the action carries legal
        // risk. Surfaced as informational only; excluded from the program
        // ceiling so we don't promise value the employer can't capture.
        positiveRoiCount: 0,
        type: 'informational',
        content: (
          <div>
            <p style={{ fontSize: 13, color: C.textMid, marginBottom: 12, lineHeight: 1.6 }}>
              <strong>{contractors.length} workers</strong> are classified as 1099 independent contractors and cannot participate in the Section 125 plan.
            </p>
            <div style={{ background: C.bg, padding: '12px 14px', borderRadius: 8, marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                Potential Impact if Reclassified as W-2
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {contractorDetails.map((e, i) => (
                  <div key={i} style={{ fontSize: 12, color: C.textMid }}>
                    <strong>{e.name}</strong>: {fmt(e.salary)} → {fmtK(e.ficaSavings)}/year employer FICA savings on {e.simulatedPlan || 'Plan 600'}
                  </div>
                ))}
              </div>
            </div>
            <div style={{ background: C.orangeLight, padding: '12px 14px', borderRadius: 8, fontSize: 11, color: C.text, lineHeight: 1.6, border: `1px solid ${C.orange}` }}>
              <div style={{ fontWeight: 700, marginBottom: 4, color: C.orange }}>
                <Icon name="warning" style={{ fontSize: 14, marginRight: 4 }} />
                Important Legal Notice
              </div>
              Worker reclassification has significant legal and tax implications. Consult with a labor law attorney before reclassifying any workers. This is provided for informational purposes only.
            </div>
          </div>
        ),
      });
    }
    
    // Recommendation 4: Plan Tier Gap Analysis
    const eligibleWithViable = analyzed.filter(e => e.eligibility?.eligible && e.hasViable && e.optimal);
    
    const activePlans = (config.planTiers || []).filter(tier => !tier.archived);
    if (eligibleWithViable.length > 0 && activePlans.length > 1) {
      // Sort plan tiers by monthly premium (ascending)
      const sortedTiers = [...activePlans].sort((a, b) => a.monthlyClaim - b.monthlyClaim);
      
      // Find employees who could benefit from moving to a higher tier
      const tierGapOpportunities = [];
      
      eligibleWithViable.forEach(e => {
        const currentTier = e.optimal;
        const currentTierIndex = sortedTiers.findIndex(t => t.label === currentTier.label);
        
        // Check if there's a higher tier available
        if (currentTierIndex >= 0 && currentTierIndex < sortedTiers.length - 1) {
          const nextTier = sortedTiers[currentTierIndex + 1];
          
          // Simulate with the next higher tier
          const nextTierResults = e.results?.find(r => r.label === nextTier.label);
          
          if (nextTierResults && nextTierResults.isViable) {
            const currentFica = currentTier.employerAnnualFica || 0;
            const nextFica = nextTierResults.employerAnnualFica || 0;
            const additionalFica = nextFica - currentFica;
            
            // Current employee benefit vs next tier benefit
            const currentBenefit = currentTier.employeeAnnualBenefit || 0;
            const nextBenefit = nextTierResults.employeeAnnualBenefit || 0;
            const additionalBenefit = nextBenefit - currentBenefit;
            
            // Only include if there's meaningful upside
            if (additionalFica > 100 || additionalBenefit > 100) {
              tierGapOpportunities.push({
                ...e,
                currentTier: currentTier.label,
                nextTier: nextTier.label,
                additionalFica,
                additionalBenefit,
                currentFica,
                nextFica,
              });
            }
          }
        }
      });
      
      if (tierGapOpportunities.length > 0) {
        // Sort by highest additional FICA savings
        const sortedOpportunities = [...tierGapOpportunities].sort((a, b) => b.additionalFica - a.additionalFica);
        const top5 = sortedOpportunities.slice(0, 5);
        const totalAdditionalFica = tierGapOpportunities.reduce((sum, e) => sum + e.additionalFica, 0);
        
        recs.push({
          id: 'plan-tier-gap',
          title: 'Plan Tier Upgrade Opportunities',
          impact: totalAdditionalFica > 5000 ? 'high' : 'medium',
          count: tierGapOpportunities.length,
          type: 'opportunity',
          content: (
            <div>
              <p style={{ fontSize: 13, color: C.textMid, marginBottom: 12, lineHeight: 1.6 }}>
                <strong>{tierGapOpportunities.length} employees</strong> are currently on viable plans but could generate additional savings by moving to a higher plan tier.
              </p>
              <div style={{ background: C.bg, padding: '12px 14px', borderRadius: 8, marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                  Top Upgrade Opportunities
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {top5.map((e, i) => (
                    <div key={i} style={{ fontSize: 12, color: C.textMid, padding: '8px 10px', background: C.card, borderRadius: 6, border: `1px solid ${C.border}` }}>
                      <div style={{ fontWeight: 700, color: C.text, marginBottom: 4 }}>
                        {e.name}: {e.currentTier} → {e.nextTier}
                      </div>
                      <div style={{ fontSize: 11, color: C.green }}>
                        Additional employer FICA: <strong>+{fmtK(e.additionalFica)}/year</strong> ({fmtK(e.currentFica)} → {fmtK(e.nextFica)})
                      </div>
                      <div style={{ fontSize: 11, color: C.textLight }}>
                        Additional employee benefit: <strong>+{fmtK(e.additionalBenefit)}/year</strong>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ background: C.greenLight, padding: '12px 14px', borderRadius: 8, marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.green, marginBottom: 6 }}>
                  Total Potential Impact
                </div>
                <div style={{ fontSize: 12, color: C.textMid, lineHeight: 1.5 }}>
                  If all {tierGapOpportunities.length} employees moved to their next viable tier: <strong>+{fmtK(totalAdditionalFica)}/year</strong> additional employer FICA savings.
                </div>
              </div>
              <div style={{ fontSize: 11, color: C.textLight, lineHeight: 1.5 }}>
                <strong>Note:</strong> Higher-tier plans offer greater benefits but may have participation implications. Review individual circumstances and ensure plan design aligns with business objectives.
              </div>
            </div>
          ),
        });
      }
    }
    
    // Recommendation 7: Union Employee Inclusion
    const unionEmployees = analyzed.filter(e => e.isUnion === true);
    const unionExcluded = unionEmployees.filter(e => e.eligibility?.status === 'union');
    
    if (unionExcluded.length > 0 && !config.employerPlanRules.eligible_classes.union_cba) {
      // Calculate potential if union employees were included
      let potentialFicaSavings = 0;
      const unionDetails = unionExcluded.slice(0, 5).map(e => {
        // Simulate with union employees allowed
        const simulated = optimizeEmployeeWithEligibility(
          e,
          {
            ...config,
            employerPlanRules: {
              ...config.employerPlanRules,
              eligible_classes: {
                ...config.employerPlanRules.eligible_classes,
                union_cba: true,
              }
            }
          }
        );
        const ficaSavings = simulated.hasViable && simulated.optimal ? simulated.optimal.employerAnnualFica : 0;
        potentialFicaSavings += ficaSavings;
        return { ...e, ficaSavings, simulatedPlan: simulated.optimal?.label };
      });
      
      // Calculate total potential for ALL union employees
      const totalPotentialFica = unionExcluded.reduce((sum, e) => {
        const simulated = optimizeEmployeeWithEligibility(
          e,
          {
            ...config,
            employerPlanRules: {
              ...config.employerPlanRules,
              eligible_classes: {
                ...config.employerPlanRules.eligible_classes,
                union_cba: true,
              }
            }
          }
        );
        return sum + (simulated.hasViable && simulated.optimal ? simulated.optimal.employerAnnualFica : 0);
      }, 0);
      
      recs.push({
        id: 'union-inclusion',
        title: 'Union (CBA) Employee Inclusion',
        impact: totalPotentialFica > 10000 ? 'high' : 'medium',
        count: unionExcluded.length,
        // CBA-driven decision (not unilateral employer choice). Surfaced as
        // an opportunity but excluded from the realistic program ceiling.
        positiveRoiCount: 0,
        type: 'policy',
        content: (
          <div>
            <p style={{ fontSize: 13, color: C.textMid, marginBottom: 12, lineHeight: 1.6 }}>
              <strong>{unionExcluded.length} union employees</strong> ({unionEmployees.length} total) are currently excluded from the Section 125 plan per the plan document.
            </p>
            <div style={{ background: C.bg, padding: '12px 14px', borderRadius: 8, marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                Potential Impact if Union Employees Included
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {unionDetails.map((e, i) => (
                  <div key={i} style={{ fontSize: 12, color: C.textMid }}>
                    <strong>{e.name}</strong>: {fmt(e.salary)} → {fmtK(e.ficaSavings)}/year employer FICA savings on {e.simulatedPlan || 'Plan 600'}
                  </div>
                ))}
                {unionExcluded.length > 5 && (
                  <div style={{ fontSize: 11, color: C.textLight, marginTop: 4 }}>
                    ... and {unionExcluded.length - 5} more union employees
                  </div>
                )}
              </div>
            </div>
            <div style={{ background: C.greenLight, padding: '12px 14px', borderRadius: 8, marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.green, marginBottom: 6 }}>
                Total Potential Employer FICA Savings
              </div>
              <div style={{ fontSize: 12, color: C.textMid, lineHeight: 1.5 }}>
                Including all {unionExcluded.length} union employees: <strong>{fmtK(totalPotentialFica)}/year</strong>
              </div>
            </div>
            <div style={{ background: C.orangeLight, padding: '12px 14px', borderRadius: 8, fontSize: 11, color: C.text, lineHeight: 1.6, border: `1px solid ${C.orange}` }}>
              <div style={{ fontWeight: 700, marginBottom: 4, color: C.orange }}>
                <Icon name="warning" style={{ fontSize: 14, marginRight: 4 }} />
                Important Compliance Notice
              </div>
              Including union employees requires negotiation with the collective bargaining representative and amendments to both the CBA and Section 125 plan document. Consult with labor counsel before making any changes.
            </div>
          </div>
        ),
      });
    }
    
    // Recommendation 8: Seasonal/Temporary Worker Inclusion
    // Detect seasonal/temp workers by employment type or low annual hours
    const seasonalTempEmployees = analyzed.filter(e => {
      // Check explicit employment type if available
      const empType = e.employmentType?.toLowerCase() || '';
      if (empType.includes('seasonal') || empType.includes('temp') || empType.includes('temporary')) {
        return true;
      }
      // Fallback: infer from annual hours (< 1500 hours/year suggests seasonal/part-time)
      if (e.annualHours && e.annualHours < 1500 && e.weeklyHours < 30) {
        return true;
      }
      return false;
    });
    
    // Find those currently excluded due to the seasonal_temp rule
    const seasonalExcluded = seasonalTempEmployees.filter(e => {
      // They would be eligible if not for the seasonal_temp exclusion
      return !config.employerPlanRules.eligible_classes.seasonal_temp && 
             e.eligibility?.status !== 'waiting' && 
             e.eligibility?.status !== 'hours' &&
             e.eligibility?.status !== '1099';
    });
    
    if (seasonalExcluded.length > 0 && !config.employerPlanRules.eligible_classes.seasonal_temp) {
      // Calculate potential if seasonal/temp employees were included
      let potentialFicaSavings = 0;
      const seasonalDetails = seasonalExcluded.slice(0, 5).map(e => {
        // Simulate with seasonal/temp allowed
        const simulated = optimizeEmployeeWithEligibility(
          e,
          {
            ...config,
            employerPlanRules: {
              ...config.employerPlanRules,
              eligible_classes: {
                ...config.employerPlanRules.eligible_classes,
                seasonal_temp: true,
              }
            }
          }
        );
        const ficaSavings = simulated.hasViable && simulated.optimal ? simulated.optimal.employerAnnualFica : 0;
        potentialFicaSavings += ficaSavings;
        return { ...e, ficaSavings, simulatedPlan: simulated.optimal?.label, hours: e.annualHours || (e.weeklyHours * 52) };
      });
      
      // Calculate total potential for ALL seasonal/temp employees
      const totalPotentialFica = seasonalExcluded.reduce((sum, e) => {
        const simulated = optimizeEmployeeWithEligibility(
          e,
          {
            ...config,
            employerPlanRules: {
              ...config.employerPlanRules,
              eligible_classes: {
                ...config.employerPlanRules.eligible_classes,
                seasonal_temp: true,
              }
            }
          }
        );
        return sum + (simulated.hasViable && simulated.optimal ? simulated.optimal.employerAnnualFica : 0);
      }, 0);
      
      recs.push({
        id: 'seasonal-temp-inclusion',
        title: 'Seasonal/Temporary Worker Inclusion',
        impact: totalPotentialFica > 8000 ? 'high' : 'medium',
        count: seasonalExcluded.length,
        // Eligibility-class change (plan-document amendment) is a policy call.
        // Not counted as positive-ROI for the program ceiling.
        positiveRoiCount: 0,
        type: 'policy',
        content: (
          <div>
            <p style={{ fontSize: 13, color: C.textMid, marginBottom: 12, lineHeight: 1.6 }}>
              <strong>{seasonalExcluded.length} seasonal or temporary employees</strong> are currently excluded from the Section 125 plan.
            </p>
            <div style={{ background: C.bg, padding: '12px 14px', borderRadius: 8, marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                Potential Impact if Seasonal/Temp Workers Included
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {seasonalDetails.map((e, i) => (
                  <div key={i} style={{ fontSize: 12, color: C.textMid }}>
                    <strong>{e.name}</strong>: {fmt(e.salary)} ({Math.round(e.hours)} hrs/year) → {fmtK(e.ficaSavings)}/year employer FICA savings on {e.simulatedPlan || 'Plan 600'}
                  </div>
                ))}
                {seasonalExcluded.length > 5 && (
                  <div style={{ fontSize: 11, color: C.textLight, marginTop: 4 }}>
                    ... and {seasonalExcluded.length - 5} more seasonal/temp employees
                  </div>
                )}
              </div>
            </div>
            <div style={{ background: C.greenLight, padding: '12px 14px', borderRadius: 8, marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.green, marginBottom: 6 }}>
                Total Potential Employer FICA Savings
              </div>
              <div style={{ fontSize: 12, color: C.textMid, lineHeight: 1.5 }}>
                Including all {seasonalExcluded.length} seasonal/temp employees: <strong>{fmtK(totalPotentialFica)}/year</strong>
              </div>
            </div>
            <div style={{ background: C.blueLight, padding: '12px 14px', borderRadius: 8, fontSize: 11, color: C.text, lineHeight: 1.6, border: `1px solid ${C.blue}` }}>
              <div style={{ fontWeight: 700, marginBottom: 4, color: C.blue }}>
                <Icon name="information-circle" style={{ fontSize: 14, marginRight: 4 }} />
                IRS Compliance Considerations
              </div>
              <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                <li>Seasonal employees working 1,000+ hours/year may trigger eligibility requirements</li>
                <li>Including seasonal workers may affect non-discrimination testing</li>
                <li>Plan document must define "seasonal employee" classification clearly</li>
                <li>Consult ERISA counsel before changing eligibility classes</li>
              </ul>
            </div>
          </div>
        ),
      });
    }
    
    // Recommendation 6: Non-Discrimination Test Preview (Simplified)
    const eligibleEmployees = analyzed.filter(e => e.eligibility?.eligible);
    const hceEmployees = analyzed.filter(e => e.eligibility?.isHCE);
    const hceEligible = hceEmployees.filter(e => e.eligibility?.eligible);
    const nonHceEmployees = analyzed.filter(e => !e.eligibility?.isHCE);
    const nonHceEligible = nonHceEmployees.filter(e => e.eligibility?.eligible);
    
    const hceEligibilityRate = hceEmployees.length > 0 ? (hceEligible.length / hceEmployees.length) * 100 : 0;
    const nonHceEligibilityRate = nonHceEmployees.length > 0 ? (nonHceEligible.length / nonHceEmployees.length) * 100 : 0;
    
    // Eligibility test: Non-HCE rate must be at least 70% of HCE rate
    const eligibilityTestPasses = nonHceEligibilityRate >= (hceEligibilityRate * 0.7);
    const eligibilityTestStatus = eligibilityTestPasses ? 'PASS' : nonHceEligibilityRate >= (hceEligibilityRate * 0.6) ? 'AT RISK' : 'FAIL';
    
    // Key Employee Concentration Test
    const keyEmployees = analyzed.filter(e => e.eligibility?.isKeyEmployee && e.eligibility?.eligible);
    let keyEmployeeBenefits = 0;
    let totalBenefits = 0;
    eligibleEmployees.forEach(e => {
      if (e.hasViable && e.optimal) {
        const benefit = e.optimal.employeeAnnualBenefit || 0;
        totalBenefits += benefit;
        if (e.eligibility?.isKeyEmployee) {
          keyEmployeeBenefits += benefit;
        }
      }
    });
    const keyEmployeeConcentration = totalBenefits > 0 ? (keyEmployeeBenefits / totalBenefits) * 100 : 0;
    const concentrationTestPasses = keyEmployeeConcentration <= config.employerPlanRules.max_key_employee_benefit_concentration;
    const hceWarningThreshold = config.advisorSettings?.hceWarningThreshold || 20;
    const concentrationTestStatus = concentrationTestPasses ? 'PASS' : keyEmployeeConcentration <= hceWarningThreshold ? 'AT RISK' : 'FAIL';
    
    if (hceEmployees.length > 0 || keyEmployees.length > 0) {
      recs.push({
        id: 'non-discrimination',
        title: 'Non-Discrimination Testing Status',
        impact: 'informational',
        count: hceEmployees.length,
        type: 'compliance',
        content: (
          <div>
            <p style={{ fontSize: 13, color: C.textMid, marginBottom: 12, lineHeight: 1.6 }}>
              Your workforce includes <strong>{hceEmployees.length} Highly Compensated Employees (HCEs)</strong> out of {analyzed.length} total employees.
            </p>
            
            {/* Eligibility Test */}
            <div style={{ 
              background: eligibilityTestStatus === 'PASS' ? C.greenLight : eligibilityTestStatus === 'AT RISK' ? C.orangeLight : C.redLight, 
              padding: '12px 14px', 
              borderRadius: 8, 
              marginBottom: 12,
              border: `1px solid ${eligibilityTestStatus === 'PASS' ? C.green : eligibilityTestStatus === 'AT RISK' ? C.orange : C.red}`
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: eligibilityTestStatus === 'PASS' ? C.green : eligibilityTestStatus === 'AT RISK' ? C.orange : C.red, marginBottom: 8 }}>
                Eligibility Test: {eligibilityTestStatus}
              </div>
              <div style={{ fontSize: 12, color: C.textMid, marginBottom: 6 }}>
                HCE eligibility rate: <strong>{hceEligible.length}/{hceEmployees.length} = {hceEligibilityRate.toFixed(1)}%</strong>
              </div>
              <div style={{ fontSize: 12, color: C.textMid, marginBottom: 6 }}>
                Non-HCE eligibility rate: <strong>{nonHceEligible.length}/{nonHceEmployees.length} = {nonHceEligibilityRate.toFixed(1)}%</strong>
              </div>
              <div style={{ fontSize: 11, color: C.textLight, marginTop: 8 }}>
                {eligibilityTestStatus === 'PASS' ? 
                  '✓ Non-HCE participation meets minimum threshold (70% of HCE rate)' :
                  eligibilityTestStatus === 'AT RISK' ?
                  '⚠ Non-HCE participation is below recommended level but within tolerance' :
                  '✗ Non-HCE participation is significantly below HCE participation'
                }
              </div>
            </div>
            
            {/* Key Employee Concentration Test */}
            <div style={{ 
              background: concentrationTestStatus === 'PASS' ? C.greenLight : concentrationTestStatus === 'AT RISK' ? C.orangeLight : C.redLight, 
              padding: '12px 14px', 
              borderRadius: 8, 
              marginBottom: 12,
              border: `1px solid ${concentrationTestStatus === 'PASS' ? C.green : concentrationTestStatus === 'AT RISK' ? C.orange : C.red}`
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: concentrationTestStatus === 'PASS' ? C.green : concentrationTestStatus === 'AT RISK' ? C.orange : C.red, marginBottom: 8 }}>
                Key Employee Concentration Test: {concentrationTestStatus}
              </div>
              <div style={{ fontSize: 12, color: C.textMid, marginBottom: 6 }}>
                Key employee benefits: <strong>{fmtK(keyEmployeeBenefits)} = {keyEmployeeConcentration.toFixed(1)}% of total benefits</strong>
              </div>
              <div style={{ fontSize: 12, color: C.textMid, marginBottom: 6 }}>
                Threshold: <strong>{config.employerPlanRules.max_key_employee_benefit_concentration}%</strong>
              </div>
              <div style={{ fontSize: 11, color: C.textLight, marginTop: 8 }}>
                {concentrationTestStatus === 'PASS' ? 
                  '✓ Key employee benefits are within allowable limit' :
                  '⚠ Key employee benefits approaching or exceeding limit'
                }
              </div>
            </div>
            
            {/* Improvement Suggestions */}
            {(eligibilityTestStatus !== 'PASS' || concentrationTestStatus !== 'PASS') && (
              <div style={{ background: C.bg, padding: '12px 14px', borderRadius: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                  To Improve Non-Discrimination Test Results:
                </div>
                <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, color: C.textMid, lineHeight: 1.8 }}>
                  <li>Increase non-HCE participation (currently {analyzed.length - hceEmployees.length - nonHceEligible.length} non-HCEs not enrolled)</li>
                  <li>Reduce waiting period to make more non-HCEs eligible sooner</li>
                  <li>Include part-time employees to broaden the eligible pool</li>
                </ul>
              </div>
            )}
            
            <div style={{ marginTop: 12, fontSize: 11, color: C.textLight, lineHeight: 1.6, fontStyle: 'italic' }}>
              <strong>Disclaimer:</strong> This is a preliminary assessment. Full Section 125 non-discrimination compliance testing should be reviewed by ERISA counsel.
            </div>
          </div>
        ),
      });
    }
    
    return recs;
  }, [analyzed, config]);
  
  // Calculate summary statistics
  const summary = useMemo(() => {
    const currentEligible = analyzed.filter(e => e.eligibility?.eligible && e.hasViable).length;
    const currentRate = analyzed.length > 0 ? (currentEligible / analyzed.length) * 100 : 0;

    // Phase 17 (UX correction): the Potential ceiling now sums only the
    // positive-ROI count from each recommendation. A recommendation engine
    // that "works for the employer's favour" should not promise a ceiling
    // built on actions that cost the employer more than they save (e.g.
    // 1099-reclass, union/seasonal inclusion). Each rec self-declares its
    // realistic contribution via `positiveRoiCount`.
    const positiveAddable = (recommendations || []).reduce((s, r) => s + (Number.isFinite(r?.positiveRoiCount) ? r.positiveRoiCount : 0), 0);
    const potentialEligible = Math.min(analyzed.length, currentEligible + positiveAddable);
    const potentialRate = analyzed.length > 0 ? (potentialEligible / analyzed.length) * 100 : 0;
    
    // Calculate potential FICA savings increase
    let additionalFica = 0;
    recommendations.forEach(rec => {
      if (rec.id === 'waiting-period' || rec.id === 'hours-threshold') {
        // Extract FICA savings from recommendation content (rough estimate)
        additionalFica += rec.count * 1500; // Conservative $1,500 per employee estimate
      }
    });
    
    const highImpact = recommendations.filter(r => r.impact === 'high').length;
    const mediumImpact = recommendations.filter(r => r.impact === 'medium').length;
    const informational = recommendations.filter(r => r.impact === 'informational').length;
    
    return {
      currentEligible,
      currentRate,
      potentialEligible,
      potentialRate,
      additionalFica,
      highImpact,
      mediumImpact,
      informational,
    };
  }, [analyzed, recommendations]);
  
  if (recommendations.length === 0) {
    return null; // No recommendations to show
  }
  
  return (
    <div style={{ ...S.card, marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <Icon name="bulb" style={{ fontSize: 24, color: C.accent }} />
        <h3 style={{ ...S.heading, margin: 0 }}>Maximize Your Program Participation</h3>
      </div>
      
      {/* Summary Card */}
      <div style={{ 
        background: `linear-gradient(135deg, ${C.accentLight} 0%, ${C.greenLight} 100%)`,
        padding: '20px 24px',
        borderRadius: 12,
        marginBottom: 20,
        border: `1px solid ${C.accentLight}`
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 20, alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 11, color: C.textLight, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              Current
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: C.accent, ...S.mono }}>
              {summary.currentEligible} of {analyzed.length}
            </div>
            <div style={{ fontSize: 12, color: C.textMid, marginTop: 2 }}>
              {summary.currentRate.toFixed(0)}% participation
            </div>
          </div>
          
          <div style={{ fontSize: 32, color: C.textLight }}>→</div>
          
          <div>
            <div style={{ fontSize: 11, color: C.textLight, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              Potential
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: C.green, ...S.mono }}>
              {summary.potentialEligible} of {analyzed.length}
            </div>
            <div style={{ fontSize: 12, color: C.textMid, marginTop: 2 }}>
              {summary.potentialRate.toFixed(0)}% participation
            </div>
          </div>
        </div>
        
        {summary.additionalFica > 0 && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 12, color: C.textMid }}>
              <strong>Additional Employer FICA Savings:</strong>
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: C.green, ...S.mono }}>
              +{fmtK(summary.additionalFica)}/year
            </div>
          </div>
        )}
        
        <div style={{ marginTop: 12, display: 'flex', gap: 16, fontSize: 11, color: C.textMid }}>
          <span><strong>{summary.highImpact}</strong> High Impact</span>
          <span>·</span>
          <span><strong>{summary.mediumImpact}</strong> Medium</span>
          <span>·</span>
          <span><strong>{summary.informational}</strong> Informational</span>
        </div>
      </div>
      
      {/* Recommendations */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {recommendations.map((rec, index) => (
          <div 
            key={rec.id} 
            style={{ 
              background: C.bg, 
              padding: '16px 18px', 
              borderRadius: 10,
              border: `1px solid ${C.border}`,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
              <div style={{
                background: rec.impact === 'high' ? C.redLight : rec.impact === 'medium' ? C.orangeLight : C.blueLight,
                color: rec.impact === 'high' ? C.red : rec.impact === 'medium' ? C.orange : C.blue,
                padding: '4px 10px',
                borderRadius: 6,
                fontSize: 10,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                whiteSpace: 'nowrap',
              }}>
                {rec.impact === 'high' ? '🔴 High Impact' : rec.impact === 'medium' ? '🟡 Medium' : '🟢 Info'}
              </div>
              <div style={{ flex: 1 }}>
                <h4 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: C.text }}>
                  {rec.title}
                </h4>
                {rec.count > 0 && (
                  <div style={{ fontSize: 11, color: C.textLight }}>
                    Affects {rec.count} employee{rec.count !== 1 ? 's' : ''}
                  </div>
                )}
              </div>
            </div>
            
            {rec.content}
          </div>
        ))}
      </div>
    </div>
  );
}
